/* ============================================================================
   PICK 4 BUNDLE BUILDER — "pick any 4 for $139" engine
   - Tabs + search/sort over server-rendered product cards
   - Load More: reveal locally first, then AJAX /collections/{handle}/products.json
   - Prefetch pipeline: hover/touch + IntersectionObserver, capped at 3 concurrent
   - Quick-buy modal: /products/{handle}.js JSON render (gallery slider, option
     pills, description, accordions cloned from template[data-pick4-accordions])
   - Slots UI + spent counter; checkout posts 4 line items (or a wrapper variant
     when data-wrapper-variant-id is set) and jumps straight to /checkout
   ========================================================================== */
(function () {
  'use strict';

  var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------------- helpers ---------------- */

  // Dollar-float amounts (products.json prices are strings like "29.95")
  function formatMoney(amount, currency) {
    var n = parseFloat(amount) || 0;
    var cur = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  // Cent amounts (/products/{handle}.js prices are integers in cents)
  function formatMoneyCents(cents) {
    return formatMoney((parseInt(cents, 10) || 0) / 100);
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function pad2(n) { return n < 10 ? '0' + n : String(n); }

  // Remove scripts/iframes/videos/payment widgets from injected fragments
  function stripUnwanted(node) {
    if (!node || !node.querySelectorAll) return;
    node.querySelectorAll('script, iframe, video, shopify-payment-terms, shopify-payment-button, .shopify-payment-button')
      .forEach(function (el) { el.remove(); });
  }

  /* ---------------- gallery slider (scroll-snap, ported from eve-bundle) ---------------- */
  function initSlider(slider) {
    if (!slider || slider.__init) return;
    slider.__init = true;
    var track = slider.querySelector('[data-slides]');
    var dots = slider.querySelectorAll('.pick4-overlay__dot');
    var prev = slider.querySelector('[data-slide-prev]');
    var next = slider.querySelector('[data-slide-next]');
    if (!track) return;

    function slideW() { return track.clientWidth || 1; }
    function index() { return Math.round(track.scrollLeft / slideW()); }
    function go(i) { track.scrollTo({ left: i * slideW(), behavior: 'smooth' }); }

    if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); go(Math.max(0, index() - 1)); });
    if (next) next.addEventListener('click', function (e) { e.stopPropagation(); go(index() + 1); });

    dots.forEach(function (d, di) {
      d.addEventListener('click', function (e) { e.stopPropagation(); go(di); });
    });

    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var i = index();
        dots.forEach(function (d, di) { d.classList.toggle('is-active', di === i); });
      });
    }, { passive: true });
  }

  /* ---------------- document-wide scroll-to-picker CTA delegation ---------------- */
  if (!window.__pick4ScrollWired) {
    window.__pick4ScrollWired = true;
    document.addEventListener('click', function (e) {
      var cta = e.target.closest && e.target.closest('[data-pick4-scroll="picker"]');
      if (!cta) return;
      var builder = document.querySelector('[data-pick4-builder]');
      if (builder) {
        e.preventDefault();
        builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ================================================================ */
  /*                          BUILDER INSTANCE                        */
  /* ================================================================ */
  function initBuilder(section) {
    if (!section || section.__pick4Init) return;
    section.__pick4Init = true;

    var BUNDLE_SIZE = parseInt(section.dataset.bundleSize, 10) || 4;
    var WRAPPER_VARIANT_ID = (section.dataset.wrapperVariantId || '').trim();
    var PROPERTY_KEY = section.dataset.propertyKey || '_pick4';
    var MAX_VISIBLE = parseInt(section.dataset.maxVisible, 10) || 24;
    var FULL_MSG = section.dataset.fullMsg || ('Your bundle is full (' + BUNDLE_SIZE + ' of ' + BUNDLE_SIZE + '). Remove a piece before adding another.');
    var ADDING_TEXT = section.dataset.addingText || 'Adding...';
    var ATC_TEXT = section.dataset.atcText || 'Add to Bundle';
    var ADDED_TEXT = section.dataset.addedText || 'Added to bundle';
    var SOLDOUT_TEXT = section.dataset.soldoutText || 'Sold out';
    var ERROR_TEXT = section.dataset.errorText || 'Something went wrong. Please try again.';

    var slots = new Array(BUNDLE_SIZE).fill(null);
    var slotEls = section.querySelectorAll('.pick4__slot');
    var counterEls = section.querySelectorAll('[data-count-num]');
    var spentEls = section.querySelectorAll('[data-spent]');
    var checkoutBtn = section.querySelector('[data-checkout-btn]');
    var errorEl = section.querySelector('[data-error-msg]');

    // Pre-formatted, market-currency spent amounts for 0..BUNDLE_SIZE pieces
    var SPENT_STEPS = [];
    try { SPENT_STEPS = JSON.parse(section.dataset.spentSteps || '[]'); } catch (e) { SPENT_STEPS = []; }

    /* ====================== TABS ====================== */
    // Non-first panes render empty server-side (Shopify caps collection.products
    // outside paginate at ~50 per page render) and hydrate here via products.json.
    var paneLoadsInFlight = {};
    function ensurePaneLoaded(pane) {
      if (!pane || pane.dataset.pageLoaded !== '0') return Promise.resolve();
      var handle = pane.dataset.collectionHandle;
      var total = parseInt(pane.dataset.productsOnPage, 10) || 0;
      if (!handle || total === 0) { pane.dataset.pageLoaded = '1'; return Promise.resolve(); }
      if (paneLoadsInFlight[handle]) return paneLoadsInFlight[handle];
      var grid = pane.querySelector('.pick4__grid');
      paneLoadsInFlight[handle] = fetch(routeRoot + 'collections/' + handle + '/products.json?limit=50&page=1')
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          var products = (data && data.products) || [];
          products.forEach(function (p, i) {
            var newCard = buildCardFromJSON(p, i);
            if (i >= MAX_VISIBLE) newCard.classList.add('pick4__card--load-more-hidden');
            grid.appendChild(newCard);
            bindCard(newCard);
            if (cardObserver) cardObserver.observe(newCard);
            refreshCardAddedState(newCard);
          });
          pane.dataset.pageLoaded = '1';
        })
        .catch(function () { delete paneLoadsInFlight[handle]; });
      return paneLoadsInFlight[handle];
    }
    section.querySelectorAll('.pick4__tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var idx = tab.dataset.tabIndex;
        section.querySelectorAll('.pick4__tab').forEach(function (t) { t.classList.remove('pick4__tab--active'); });
        tab.classList.add('pick4__tab--active');
        section.querySelectorAll('.pick4__pane').forEach(function (p) { p.classList.remove('pick4__pane--active'); });
        var pane = section.querySelector('.pick4__pane[data-pane="' + idx + '"]');
        if (pane) {
          pane.classList.add('pick4__pane--active');
          ensurePaneLoaded(pane).then(applyFiltersAndSort);
        }
        applyFiltersAndSort();
      });
    });
    // Hydrate de overige panes alvast in idle-tijd zodat tab-switches instant zijn
    (window.requestIdleCallback || function (f) { setTimeout(f, 1200); })(function () {
      section.querySelectorAll('.pick4__pane[data-page-loaded="0"]').forEach(function (p) { ensurePaneLoaded(p); });
    });

    /* ====================== SEARCH + SORT ====================== */
    var searchInput = section.querySelector('[data-pick4-search]');
    var sortSelect = section.querySelector('[data-pick4-sort]');
    if (searchInput) searchInput.addEventListener('input', applyFiltersAndSort);
    if (sortSelect) sortSelect.addEventListener('change', applyFiltersAndSort);

    function applyFiltersAndSort() {
      var query = searchInput ? searchInput.value.trim().toLowerCase() : '';
      var sortVal = sortSelect ? sortSelect.value : 'featured';
      var activePane = section.querySelector('.pick4__pane--active');
      if (!activePane) return;
      var grid = activePane.querySelector('.pick4__grid');
      if (!grid) return;
      var cards = Array.from(grid.querySelectorAll('.pick4__card'));
      var visible = [];
      cards.forEach(function (card) {
        var match = !query || (card.dataset.productTitle || '').toLowerCase().indexOf(query) !== -1;
        card.classList.toggle('pick4__card--hidden', !match);
        if (match) visible.push(card);
      });
      // Featured restores the original server-rendered order
      visible.sort(function (a, b) {
        if (sortVal === 'featured') return parseInt(a.dataset.originalOrder, 10) - parseInt(b.dataset.originalOrder, 10);
        if (sortVal === 'price-asc') return parseFloat(a.dataset.productPrice) - parseFloat(b.dataset.productPrice);
        if (sortVal === 'price-desc') return parseFloat(b.dataset.productPrice) - parseFloat(a.dataset.productPrice);
        if (sortVal === 'name-asc') return (a.dataset.productTitle || '').localeCompare(b.dataset.productTitle || '');
        return 0;
      });
      visible.forEach(function (c) { grid.appendChild(c); });
      var noRes = section.querySelector('[data-no-results]');
      if (noRes) noRes.hidden = !(visible.length === 0 && cards.length > 0);
    }

    /* ====================== CARD BINDING + PREFETCH ====================== */
    // Sold-out label renders via CSS (.pick4__card--soldout .pick4__card-img-wrap::after);
    // JS only toggles the class — no element injection (avoids a double label).

    function bindCard(card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('.pick4__card-add')) return;
        e.preventDefault();
        openModalForCard(card);
      });
      var addBtn = card.querySelector('.pick4__card-add');
      if (addBtn) addBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        openModalForCard(card);
      });
      // Hover prefetch (desktop) — 150ms intent delay
      var prefetchTimer = null;
      card.addEventListener('mouseenter', function () {
        prefetchTimer = setTimeout(function () { prefetchProduct(card.dataset.productHandle); }, 150);
      });
      card.addEventListener('mouseleave', function () {
        if (prefetchTimer) clearTimeout(prefetchTimer);
      });
      // Touch prefetch (mobile)
      card.addEventListener('touchstart', function () {
        prefetchProduct(card.dataset.productHandle);
      }, { passive: true });
    }
    section.querySelectorAll('.pick4__card').forEach(bindCard);

    /* ====================== LOAD MORE ====================== */

    // Build a product card from /collections/{handle}/products.json data
    function buildCardFromJSON(p, originalOrder) {
      var bundleMax = 0;
      if (p.tags && p.tags.length) {
        var tagStr = Array.isArray(p.tags) ? p.tags.join(',') : String(p.tags);
        var maxMatch = tagStr.toLowerCase().match(/bundle-max-([1-3])/);
        if (maxMatch) bundleMax = parseInt(maxMatch[1], 10);
      }
      var firstVar = (p.variants && p.variants[0]) || {};
      var price = firstVar.price ? parseFloat(firstVar.price) : 0;
      var compareAt = firstVar.compare_at_price ? parseFloat(firstVar.compare_at_price) : 0;
      var available = (p.variants || []).some(function (v) { return v.available !== false; });
      var img = '';
      if (p.images && p.images.length) {
        img = typeof p.images[0] === 'string' ? p.images[0] : (p.images[0].src || '');
      } else if (p.featured_image) {
        img = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.src || '');
      }
      // Color swatches from the Color/Colour/Finish/Metal option values
      var colorValues = '';
      (p.options || []).forEach(function (opt) {
        var name = (typeof opt === 'string' ? opt : opt.name) || '';
        var lower = name.toLowerCase();
        if (lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1 || lower.indexOf('finish') !== -1 || lower.indexOf('metal') !== -1) {
          var values = (typeof opt === 'object' && opt.values) ? opt.values : [];
          colorValues = values.join(',');
        }
      });
      var card = document.createElement('div');
      card.className = 'pick4__card' + (available ? '' : ' pick4__card--soldout');
      card.dataset.productId = p.id || '';
      card.dataset.productHandle = p.handle || '';
      card.dataset.productTitle = p.title || '';
      card.dataset.productPrice = String(price);
      card.dataset.originalOrder = String(originalOrder);
      if (bundleMax > 0) card.dataset.bundleMax = String(bundleMax);
      var swatchHTML = '';
      if (colorValues) {
        swatchHTML = '<div class="pick4__card-swatches">' +
          colorValues.split(',').slice(0, 4).map(function (f) {
            var v = esc(f.trim());
            return '<span class="pick4__card-swatch" data-color="' + v + '" title="' + v + '"></span>';
          }).join('') +
          '</div>';
      }
      var priceHTML = (compareAt > price ? '<s>' + formatMoney(compareAt) + '</s> ' : '') + '<strong>' + formatMoney(price) + '</strong>';
      var titleEsc = esc(p.title || '');
      card.innerHTML =
        '<div class="pick4__card-img-wrap">' +
          '<img class="pick4__card-img" src="' + img + '" alt="' + titleEsc + '" loading="lazy">' +
          '<button type="button" class="pick4__card-add" aria-label="Add to bundle"></button>' +
        '</div>' +
        '<div class="pick4__card-info">' +
          '<h3 class="pick4__card-title">' + titleEsc + '</h3>' +
          '<div class="pick4__card-meta">' +
            (swatchHTML || '<span></span>') +
            '<p class="pick4__card-price">' + priceHTML + '</p>' +
          '</div>' +
        '</div>';
      return card;
    }

    section.querySelectorAll('[data-load-more]').forEach(function (btn) {
      btn.addEventListener('click', async function () {
        var pane = btn.closest('.pick4__pane');
        if (!pane) return;
        var grid = pane.querySelector('.pick4__grid');
        if (!grid) return;
        var visibleCount = parseInt(grid.dataset.visibleCount, 10) || MAX_VISIBLE;
        var hiddenCards = grid.querySelectorAll('.pick4__card--load-more-hidden');
        // Reveal the next chunk from the local (server-rendered) cache first
        var justRevealed = Array.from(hiddenCards).slice(0, MAX_VISIBLE);
        justRevealed.forEach(function (c) {
          c.classList.remove('pick4__card--load-more-hidden');
        });
        grid.dataset.visibleCount = visibleCount + MAX_VISIBLE;
        // Warm the modal cache for the just-revealed cards (queue caps at 3)
        justRevealed.forEach(function (c) {
          var handle = c.dataset.productHandle;
          if (handle) prefetchProduct(handle);
        });

        // All local cards revealed? AJAX-fetch the next page via products.json
        var stillHidden = grid.querySelectorAll('.pick4__card--load-more-hidden').length;
        if (stillHidden === 0) {
          var pageLoaded = parseInt(pane.dataset.pageLoaded, 10) || 1;
          var collectionHandle = pane.dataset.collectionHandle;
          if (!collectionHandle) { btn.style.display = 'none'; return; }
          var nextPage = pageLoaded + 1;
          var cacheKey = collectionHandle + '_' + nextPage;
          var products = null;

          // Fast path: the IntersectionObserver prefetch already has the data
          if (loadMoreCache[cacheKey]) {
            products = loadMoreCache[cacheKey];
          } else {
            // Slow path: cache miss, fetch live with a loading state
            var origLabel = btn.textContent;
            btn.textContent = 'Loading...';
            btn.disabled = true;
            try {
              var url = routeRoot + 'collections/' + collectionHandle + '/products.json?limit=50&page=' + nextPage;
              var resp = await fetch(url);
              if (!resp.ok) throw new Error('HTTP ' + resp.status);
              var data = await resp.json();
              products = data && data.products ? data.products : null;
            } catch (err) {
              // Graceful hide — no dead button when the endpoint fails
              btn.style.display = 'none';
              btn.textContent = origLabel;
              btn.disabled = false;
              return;
            }
            btn.textContent = origLabel;
            btn.disabled = false;
          }

          if (!products || products.length === 0) {
            btn.style.display = 'none';
          } else {
            products.forEach(function (p) {
              var newCard = buildCardFromJSON(p, grid.querySelectorAll('.pick4__card').length);
              grid.appendChild(newCard);
              bindCard(newCard);
              if (cardObserver) cardObserver.observe(newCard);
            });
            pane.dataset.pageLoaded = String(nextPage);
            if (products.length < 50) btn.style.display = 'none';
            // Prefetch the NEXT page so subsequent clicks are also instant
            setTimeout(function () { prefetchNextPage(pane); }, 100);
          }
        }
      });
    });

    // Initially hide load-more when everything already fits in the first chunk
    section.querySelectorAll('.pick4__pane').forEach(function (pane) {
      var grid = pane.querySelector('.pick4__grid');
      var btn = pane.querySelector('[data-load-more]');
      if (grid && btn) {
        var totalCards = grid.querySelectorAll('.pick4__card').length;
        var totalProducts = parseInt(pane.dataset.productsOnPage, 10) || totalCards;
        if (totalCards <= MAX_VISIBLE && totalProducts <= totalCards) btn.style.display = 'none';
      }
    });

    /* ====================== PREFETCH PIPELINE (product JSON) ====================== */
    var productCache = {};     // handle -> product JSON (/products/{handle}.js)
    var prefetchInFlight = {}; // handle -> true while fetching
    var fetchQueue = [];       // handles waiting for a fetch slot
    var activeFetches = 0;
    var MAX_PREFETCH_CONCURRENCY = 3;

    function prefetchProduct(handle) {
      if (!handle || productCache[handle] || prefetchInFlight[handle]) return;
      if (fetchQueue.indexOf(handle) !== -1) return;
      fetchQueue.push(handle);
      pumpFetch();
    }
    function pumpFetch() {
      while (activeFetches < MAX_PREFETCH_CONCURRENCY && fetchQueue.length > 0) {
        var handle = fetchQueue.shift();
        if (!handle || productCache[handle] || prefetchInFlight[handle]) continue;
        startPrefetch(handle);
      }
    }
    function startPrefetch(handle) {
      prefetchInFlight[handle] = true;
      activeFetches++;
      fetch(routeRoot + 'products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) {
          if (json) productCache[handle] = json;
        })
        .catch(function () {})
        .then(function () {
          delete prefetchInFlight[handle];
          activeFetches--;
          pumpFetch();
        });
    }

    // Wait for a condition (tap landed while that product's prefetch is mid-flight)
    function waitFor(check, timeoutMs) {
      return new Promise(function (resolve) {
        var waited = 0;
        (function poll() {
          if (check()) return resolve(true);
          if (waited >= timeoutMs) return resolve(false);
          waited += 100;
          setTimeout(poll, 100);
        })();
      });
    }

    // Warm the cache for the first visible chunk on page load / tab change
    function bulkPrefetchVisible() {
      var activePane = section.querySelector('.pick4__pane--active');
      if (!activePane) return;
      var cards = activePane.querySelectorAll('.pick4__card:not(.pick4__card--load-more-hidden)');
      Array.from(cards).slice(0, MAX_VISIBLE).map(function (c) { return c.dataset.productHandle; })
        .filter(Boolean)
        .forEach(function (h) { prefetchProduct(h); });
    }
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(bulkPrefetchVisible, { timeout: 2000 });
    } else {
      setTimeout(bulkPrefetchVisible, 1500);
    }
    section.querySelectorAll('.pick4__tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        setTimeout(bulkPrefetchVisible, 100);
      });
    });

    /* ====================== INTERSECTION OBSERVERS ====================== */
    var loadMoreCache = {}; // { collectionHandle_page: [...productsJSON] }
    function prefetchNextPage(pane) {
      if (!pane) return;
      var collectionHandle = pane.dataset.collectionHandle;
      if (!collectionHandle) return;
      var pageLoaded = parseInt(pane.dataset.pageLoaded, 10) || 1;
      var nextPage = pageLoaded + 1;
      var cacheKey = collectionHandle + '_' + nextPage;
      if (loadMoreCache[cacheKey]) return;
      if (pane.dataset.prefetchInFlight === cacheKey) return;
      pane.dataset.prefetchInFlight = cacheKey;
      var url = routeRoot + 'collections/' + collectionHandle + '/products.json?limit=50&page=' + nextPage;
      fetch(url).then(function (r) { return r.ok ? r.json() : null; }).then(function (data) {
        if (data && data.products) loadMoreCache[cacheKey] = data.products;
        pane.dataset.prefetchInFlight = '';
      }).catch(function () {
        pane.dataset.prefetchInFlight = '';
      });
    }

    var cardObserver = null;
    if ('IntersectionObserver' in window) {
      // CARD observer — prefetch product JSON when a card nears the viewport
      cardObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target;
          var handle = card.dataset.productHandle;
          if (handle) prefetchProduct(handle);
          cardObserver.unobserve(card);
        });
      }, { rootMargin: '300px 0px', threshold: 0.01 });

      var observeAllCards = function () {
        // Hidden (--load-more-hidden) cards have no layout, so the observer only
        // fires for them once Load More reveals them — safe to observe them all.
        section.querySelectorAll('.pick4__card').forEach(function (card) {
          cardObserver.observe(card);
        });
      };
      observeAllCards();
      section.querySelectorAll('.pick4__tab').forEach(function (tab) {
        tab.addEventListener('click', function () { setTimeout(observeAllCards, 50); });
      });

      // LOAD-MORE observer — prefetch the next products.json page ahead of the click
      var loadMoreObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var pane = entry.target.closest('.pick4__pane');
          prefetchNextPage(pane);
        });
      }, { rootMargin: '400px 0px', threshold: 0.01 });
      section.querySelectorAll('[data-load-more]').forEach(function (btn) {
        loadMoreObserver.observe(btn);
      });
    }

    /* ====================== QUICK-BUY MODAL ====================== */
    var overlay = section.querySelector('[data-pick4-overlay]');
    var accordionTpl = section.querySelector('template[data-pick4-accordions]');
    var activeCardForModal = null;

    function buildOverlay() {
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.setAttribute('data-pick4-overlay', '');
        overlay.hidden = true;
        document.body.appendChild(overlay);
      }
      if (overlay.__built) return overlay;
      overlay.__built = true;
      overlay.classList.add('pick4-overlay');
      // Fullscreen white overlay skeleton — layout-critical styles inline so the
      // modal works even before the stylesheet finishes loading (Olivia parity)
      overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;width:100%;height:100dvh;background:#fff;z-index:500;display:none;align-items:flex-start;justify-content:center;overflow-y:auto;padding:32px 16px';
      overlay.innerHTML =
        '<div class="pick4-overlay__panel" style="background:#fff;width:100%;max-width:1080px;position:relative;border-radius:2px;margin:0 auto;z-index:501">' +
          '<button type="button" class="pick4-overlay__close" aria-label="Close" style="position:absolute;top:14px;right:14px;width:36px;height:36px;border-radius:50%;background:#1a1a1a;color:#fff;border:none;font-size:20px;cursor:pointer;z-index:10;display:flex;align-items:center;justify-content:center;line-height:1;padding:0;">&times;</button>' +
          '<div class="pick4-overlay__loader" style="padding:60px;text-align:center;color:#666;"><div style="display:inline-block;width:32px;height:32px;border:3px solid #e8e3dc;border-top-color:#b8956a;border-radius:50%;animation:pick4spin 1s linear infinite"></div><p style="margin-top:14px;font-size:13px;letter-spacing:1px;text-transform:uppercase">Loading product&hellip;</p></div>' +
          '<div class="pick4-overlay__body"></div>' +
        '</div>' +
        '<style>@keyframes p4spin{to{transform:rotate(360deg)}}</style>';
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || e.target.classList.contains('pick4-overlay__close')) closeOverlay();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && overlay.style.display === 'flex') closeOverlay();
      });
      // When an accordion opens near the bottom of the modal, its content expands
      // below the fold — nudge the overlay scroll so the answer is visible.
      // 'toggle' doesn't bubble, so listen in capture phase; scroll after the
      // open animation lands (400ms) + a 750ms safety nudge for slower devices.
      overlay.addEventListener('toggle', function (e) {
        var d = e.target;
        if (!d || d.tagName !== 'DETAILS' || !d.open) return;
        var nudge = function () {
          if (!d.open) return;
          try { d.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (err) {}
        };
        setTimeout(nudge, 400);
        setTimeout(nudge, 750);
      }, true);
      return overlay;
    }

    function openOverlay() {
      var ov = buildOverlay();
      ov.hidden = false;
      ov.style.display = 'flex';
      // Aggressive iOS-safe body scroll lock — overflow:hidden alone is not
      // enough on iOS Safari. Save scroll position and anchor the body in place.
      var scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.dataset.pick4SavedScroll = String(scrollY);
      document.body.style.cssText =
        'overflow:hidden;position:fixed;top:-' + scrollY + 'px;left:0;right:0;width:100%';
      // Show "bundle full" warning at the top if all slots are filled
      var bundleFull = slots.filter(function (s) { return s !== null; }).length >= BUNDLE_SIZE;
      if (bundleFull) {
        showModalError(FULL_MSG);
      } else {
        hideModalError();
      }
    }

    function closeOverlay() {
      if (!overlay || !overlay.__built) return;
      overlay.style.display = 'none';
      overlay.hidden = true;
      var body = overlay.querySelector('.pick4-overlay__body');
      var loader = overlay.querySelector('.pick4-overlay__loader');
      if (body) body.innerHTML = '';
      if (loader) {
        loader.style.display = 'block';
        loader.innerHTML = '<div style="display:inline-block;width:32px;height:32px;border:3px solid #e8e3dc;border-top-color:#b8956a;border-radius:50%;animation:pick4spin 1s linear infinite"></div><p style="margin-top:14px;font-size:13px;letter-spacing:1px;text-transform:uppercase">Loading product&hellip;</p>';
      }
      // Restore body scroll position (paired with openOverlay's fixed-body lock)
      var saved = parseInt(document.body.dataset.pick4SavedScroll || '0', 10);
      document.body.style.cssText = '';
      delete document.body.dataset.pick4SavedScroll;
      if (saved) window.scrollTo(0, saved);
    }

    function showModalError(msg) {
      var ov = buildOverlay();
      var panel = ov.querySelector('.pick4-overlay__panel');
      var existing = panel.querySelector('.pick4-overlay__error');
      if (existing) {
        existing.textContent = msg;
        return;
      }
      var bar = document.createElement('div');
      bar.className = 'pick4-overlay__error';
      bar.style.cssText = 'background:#fef3f2;border-bottom:1px solid #fecaca;color:#9b3a3a;padding:12px 20px;font-size:13px;font-weight:500;text-align:center;line-height:1.4;position:sticky;top:0;z-index:5';
      bar.textContent = msg;
      panel.insertBefore(bar, panel.firstChild);
    }

    function hideModalError() {
      if (!overlay || !overlay.__built) return;
      var existing = overlay.querySelector('.pick4-overlay__error');
      if (existing) existing.remove();
    }

    /* ---------- modal render (from /products/{handle}.js JSON) ---------- */

    function variantData(panel) {
      try { return JSON.parse(panel.querySelector('[data-variant-json]').textContent); }
      catch (e) { return []; }
    }

    function selectDefaults(panel) {
      var data = variantData(panel);
      var first = null;
      for (var i = 0; i < data.length; i++) { if (data[i].available) { first = data[i]; break; } }
      if (!first) first = data[0];
      if (!first) return;
      panel.querySelectorAll('[data-option-index]').forEach(function (group, gi) {
        var want = first.options[gi];
        group.querySelectorAll('[data-option-value]').forEach(function (b) {
          b.classList.toggle('is-selected', b.getAttribute('data-option-value') === want);
        });
      });
      resolveVariant(panel);
    }

    function resolveVariant(panel) {
      if (!panel) return;
      var selected = [];
      var complete = true;
      panel.querySelectorAll('[data-option-index]').forEach(function (g) {
        var sel = g.querySelector('.is-selected');
        selected.push(sel ? sel.getAttribute('data-option-value') : null);
        if (!sel) complete = false;
      });

      var atcBtn = panel.querySelector('[data-atc]');
      if (!atcBtn) return;
      if (!complete) {
        atcBtn.disabled = true;
        atcBtn.removeAttribute('data-variant-id');
        atcBtn.textContent = ATC_TEXT;
        return;
      }

      var match = variantData(panel).find(function (v) {
        return v.options.length === selected.length && v.options.every(function (o, i) { return o === selected[i]; });
      });

      var priceEl = panel.querySelector('[data-modal-price]');
      if (match) {
        if (priceEl) {
          priceEl.innerHTML = (match.compare_at_price && match.compare_at_price > match.price
            ? '<s>' + formatMoneyCents(match.compare_at_price) + '</s> ' : '') +
            '<strong>' + formatMoneyCents(match.price) + '</strong>';
        }
        // Swap gallery to the variant's own image when it has one
        if (match.featured_image && match.featured_image.src) {
          var track = panel.parentElement ? panel.parentElement.querySelector('[data-slides]') : null;
          var firstImg = track ? track.querySelector('img') : null;
          if (firstImg && firstImg.src !== match.featured_image.src) firstImg.src = match.featured_image.src;
        }
      }

      if (match && match.available) {
        atcBtn.disabled = false;
        atcBtn.setAttribute('data-variant-id', match.id);
        atcBtn.textContent = ATC_TEXT;
      } else {
        atcBtn.disabled = true;
        atcBtn.removeAttribute('data-variant-id');
        atcBtn.textContent = match ? SOLDOUT_TEXT : ATC_TEXT;
      }
    }

    function renderModal(p) {
      var ov = buildOverlay();
      var body = ov.querySelector('.pick4-overlay__body');

      var imgs = (p.media || []).filter(function (m) { return m.media_type === 'image'; });
      if (!imgs.length && p.images && p.images.length) {
        imgs = p.images.map(function (src) { return { src: src }; });
      }

      // Gallery — scroll-snap slider (same mechanics as eve-bundle)
      var g = '<div class="pick4-overlay__gallery"><div class="pick4-overlay__slider" data-slider><div class="pick4-overlay__slides" data-slides>';
      imgs.forEach(function (m) {
        var src = m.src.indexOf('?') > -1 ? m.src + '&width=700' : m.src + '?width=700';
        g += '<div class="pick4-overlay__slide"><img src="' + src + '" alt="' + esc(p.title) + '" loading="lazy"></div>';
      });
      g += '</div>';
      if (imgs.length > 1) {
        g += '<button type="button" class="pick4-overlay__arrow pick4-overlay__arrow--prev" data-slide-prev aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg></button>';
        g += '<button type="button" class="pick4-overlay__arrow pick4-overlay__arrow--next" data-slide-next aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg></button>';
        g += '<div class="pick4-overlay__dots" data-dots>';
        imgs.forEach(function (_, i) { g += '<span class="pick4-overlay__dot' + (i === 0 ? ' is-active' : '') + '"></span>'; });
        g += '</div>';
      }
      g += '</div></div>';

      // Info column — title, price, option pills, ATC, description
      var d = '<div class="pick4-overlay__info" data-variants-panel>';
      d += '<h2 class="pick4-overlay__title">' + esc(p.title) + '</h2>';
      var priceHTML = (p.compare_at_price && p.compare_at_price > p.price
        ? '<s>' + formatMoneyCents(p.compare_at_price) + '</s> ' : '') +
        '<strong>' + formatMoneyCents(p.price) + '</strong>';
      d += '<p class="pick4-overlay__price" data-modal-price>' + priceHTML + '</p>';

      var hasOptions = !(p.variants.length === 1 && p.variants[0].title === 'Default Title');
      if (hasOptions) {
        p.options.forEach(function (opt, idx) {
          var name = opt.name || opt;
          var values = opt.values || [];
          d += '<fieldset class="pick4-overlay__optgroup" data-option-index="' + idx + '"><legend class="pick4-overlay__optname">' + esc(name) + '</legend><div class="pick4-overlay__optvalues">';
          values.forEach(function (v) {
            d += '<button type="button" class="pick4-overlay__optvalue" data-option-value="' + esc(v) + '">' + esc(v) + '</button>';
          });
          d += '</div></fieldset>';
        });
      }

      var variantJson = p.variants.map(function (v) {
        return { id: v.id, available: v.available, options: v.options, price: v.price, compare_at_price: v.compare_at_price, featured_image: v.featured_image ? { src: v.featured_image.src } : null };
      });
      d += '<script type="application/json" data-variant-json>' + JSON.stringify(variantJson) + '<\/script>';
      d += '<button type="button" class="pick4-overlay__atc" data-atc disabled>' + esc(ATC_TEXT) + '</button>';

      if (p.description && p.description.trim()) {
        d += '<div class="pick4-overlay__desc" data-modal-desc></div>';
      }
      d += '<div class="pick4-overlay__accordions" data-modal-accordions></div>';
      d += '</div>';

      body.innerHTML = '<div class="pick4-overlay__grid">' + g + d + '</div>';

      // Description injected via DOM so we can strip scripts/iframes safely
      if (p.description && p.description.trim()) {
        var descEl = body.querySelector('[data-modal-desc]');
        if (descEl) {
          descEl.innerHTML = p.description;
          stripUnwanted(descEl);
        }
      }

      // Accordions — clone the server-rendered block accordions from the template
      var accWrap = body.querySelector('[data-modal-accordions]');
      if (accWrap && accordionTpl && accordionTpl.content) {
        accWrap.appendChild(accordionTpl.content.cloneNode(true));
      }

      body.querySelectorAll('[data-slider]').forEach(initSlider);

      var panel = body.querySelector('[data-variants-panel]');
      panel.addEventListener('click', function (e) {
        var optBtn = e.target.closest('[data-option-value]');
        if (optBtn) {
          var group = optBtn.closest('[data-option-index]');
          if (!group) return;
          group.querySelectorAll('[data-option-value]').forEach(function (b) { b.classList.remove('is-selected'); });
          optBtn.classList.add('is-selected');
          resolveVariant(panel);
          return;
        }
        var atc = e.target.closest('[data-atc]');
        if (atc && !atc.disabled) addToBundle(atc, panel);
      });

      if (hasOptions) {
        selectDefaults(panel);
      } else if (variantJson.length) {
        var atcBtn = panel.querySelector('[data-atc]');
        if (variantJson[0].available) {
          atcBtn.disabled = false;
          atcBtn.setAttribute('data-variant-id', variantJson[0].id);
        } else {
          atcBtn.disabled = true;
          atcBtn.textContent = SOLDOUT_TEXT;
        }
      }
    }

    async function openModalForCard(card) {
      activeCardForModal = card;
      var handle = card.dataset.productHandle;
      if (!handle) return;
      openOverlay();
      var ov = buildOverlay();
      var loader = ov.querySelector('.pick4-overlay__loader');
      var body = ov.querySelector('.pick4-overlay__body');
      body.innerHTML = '';

      try {
        var product = productCache[handle];
        // If a background prefetch is mid-flight, give it a moment to land
        // instead of firing a duplicate download.
        if (!product && prefetchInFlight[handle]) {
          await waitFor(function () { return !!productCache[handle]; }, 1500);
          product = productCache[handle];
        }
        if (!product) {
          // Direct fetch — intentionally BYPASSES the prefetch queue so a tap
          // never waits behind background prefetches.
          var resp = await fetch(routeRoot + 'products/' + handle + '.js');
          if (!resp.ok) throw new Error('Fetch failed: ' + resp.status);
          product = await resp.json();
          productCache[handle] = product;
        }

        loader.style.display = 'none';
        renderModal(product);
      } catch (err) {
        loader.innerHTML = '<p style="color:#9b3a3a">Could not load product. Please try again.</p>';
      }
    }

    /* ====================== ADD TO BUNDLE ====================== */

    function countProductInSlots(productId) {
      return slots.filter(function (s) { return s && String(s.productId) === String(productId); }).length;
    }

    function refreshCardAddedState(card) {
      if (!card) return;
      var addBtn = card.querySelector('.pick4__card-add');
      if (!addBtn) return;
      var count = countProductInSlots(card.dataset.productId);
      addBtn.innerHTML = ''; // the +/checkmark is drawn entirely via CSS
      if (count === 0) {
        addBtn.classList.remove('pick4__card-add--added');
        addBtn.setAttribute('aria-label', 'Add to bundle');
      } else {
        addBtn.classList.add('pick4__card-add--added');
        addBtn.setAttribute('aria-label', count > 1 ? (ADDED_TEXT + ' (' + count + ')') : ADDED_TEXT);
      }
    }

    function addToBundle(atcBtn, panel) {
      var card = activeCardForModal;
      if (!card) return;
      var variantId = atcBtn.getAttribute('data-variant-id');
      if (!variantId) return;

      var slotIdx = slots.findIndex(function (s) { return s === null; });
      if (slotIdx === -1) {
        showModalError(FULL_MSG);
        return;
      }
      // Per-product bundle limit — products tagged bundle-max-N can be added
      // at most N times per bundle (any variant counts toward the same limit)
      var bundleMax = parseInt(card.dataset.bundleMax || '0', 10);
      if (bundleMax > 0 && countProductInSlots(card.dataset.productId) >= bundleMax) {
        var remaining = BUNDLE_SIZE - slots.filter(function (s) { return s !== null; }).length;
        showModalError('You can add up to ' + bundleMax + ' of this piece per bundle. Pick a different piece for your remaining slot' + (remaining > 1 ? 's' : '') + '.');
        return;
      }

      // Variant title from the selected pills
      var variantTitle = '';
      var pickedOptionLabels = [];
      panel.querySelectorAll('[data-option-index] .is-selected').forEach(function (el) {
        var t = (el.textContent || '').trim();
        if (t) pickedOptionLabels.push(t);
      });
      if (pickedOptionLabels.length) variantTitle = pickedOptionLabels.join(' / ');

      // Price of the matched variant (cents)
      var price = 0;
      var match = variantData(panel).find(function (v) { return String(v.id) === String(variantId); });
      if (match) price = match.price;

      // Image: variant image > current gallery image > card image
      var cardImg = card.querySelector('.pick4__card-img');
      var image = cardImg ? cardImg.getAttribute('src') : '';
      if (match && match.featured_image && match.featured_image.src) {
        image = match.featured_image.src;
      } else {
        var body = overlay.querySelector('.pick4-overlay__body');
        var galleryImg = body ? body.querySelector('[data-slides] img') : null;
        if (galleryImg && galleryImg.src) image = galleryImg.src;
      }

      slots[slotIdx] = {
        productId: card.dataset.productId,
        variantId: String(variantId),
        title: card.dataset.productTitle,
        variantTitle: variantTitle,
        price: price,
        image: image,
        cardEl: card
      };
      refreshCardAddedState(card);
      updateSlotsUI();
      closeOverlay();
    }

    /* ====================== SLOT UI ====================== */
    // Slot DOM: persistent italic number top-left, then either an empty-state
    // plus or a filled image + remove button. Number stays put across states.
    function updateSlotsUI() {
      slotEls.forEach(function (slotEl, i) {
        var data = slots[i];
        // Remove only the dynamic children — the persistent number span stays
        Array.from(slotEl.children).forEach(function (child) {
          if (!child.classList.contains('pick4__slot-num')) slotEl.removeChild(child);
        });
        // Ensure the number exists (in case it was somehow removed)
        if (!slotEl.querySelector('.pick4__slot-num')) {
          var num = document.createElement('span');
          num.className = 'pick4__slot-num';
          num.textContent = pad2(i + 1);
          slotEl.insertBefore(num, slotEl.firstChild);
        }
        if (data) {
          slotEl.classList.add('pick4__slot--filled');
          var img = document.createElement('img');
          img.className = 'pick4__slot-img';
          img.src = data.image || '';
          img.alt = data.title;
          slotEl.appendChild(img);
          var rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'pick4__slot-remove';
          rm.setAttribute('aria-label', 'Remove');
          // No innerHTML — the X is drawn via CSS ::before/::after pseudo-elements
          rm.addEventListener('click', function (e) { e.stopPropagation(); removeSlot(i); });
          slotEl.appendChild(rm);
        } else {
          slotEl.classList.remove('pick4__slot--filled');
          var empty = document.createElement('div');
          empty.className = 'pick4__slot-empty';
          empty.innerHTML = '<span class="pick4__slot-plus"></span>';
          slotEl.appendChild(empty);
        }
      });
      var count = slots.filter(function (s) { return s !== null; }).length;
      counterEls.forEach(function (el) { el.textContent = count; });
      // Spent display: pick the pre-formatted market-currency amount
      var spentDisplay = SPENT_STEPS[count];
      if (spentDisplay) spentEls.forEach(function (el) { el.textContent = spentDisplay; });
      var ready = count === BUNDLE_SIZE;
      if (checkoutBtn) {
        checkoutBtn.disabled = !ready;
        checkoutBtn.classList.toggle('pick4__checkout-btn--active', ready);
      }
    }

    // Removing compacts the remaining pieces forward (reference behavior)
    function removeSlot(idx) {
      var removed = slots[idx];
      slots[idx] = null;
      var filled = slots.filter(function (s) { return s !== null; });
      while (filled.length < BUNDLE_SIZE) filled.push(null);
      slots = filled;
      if (removed && removed.cardEl) refreshCardAddedState(removed.cardEl);
      updateSlotsUI();
    }

    // Tapping an empty slot scrolls to the active product grid
    slotEls.forEach(function (slotEl) {
      slotEl.addEventListener('click', function (e) {
        if (e.target.closest('.pick4__slot-remove')) return;
        if (slotEl.classList.contains('pick4__slot--filled')) return;
        var grid = section.querySelector('.pick4__pane--active .pick4__grid');
        if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    /* ====================== ERRORS ====================== */
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.style.display = msg ? 'block' : 'none';
    }

    /* ====================== FINAL CHECKOUT ====================== */
    if (checkoutBtn) checkoutBtn.addEventListener('click', function () {
      if (slots.some(function (s) { return s === null; })) return;
      showError('');
      checkoutBtn.disabled = true;
      var origText = checkoutBtn.textContent;
      checkoutBtn.textContent = ADDING_TEXT;

      var payload;
      if (WRAPPER_VARIANT_ID) {
        // Wrapper path: one wrapper line item carrying the picked variant ids
        // (a discount function expands it at checkout)
        var lineItemProperties = {};
        slots.forEach(function (s, i) {
          var n = i + 1;
          // Hidden — the function reads these to expand the wrapper at checkout
          lineItemProperties['_id' + n] = String(s.variantId);
          // Visible — minimal customer-facing summary on the cart page
          lineItemProperties['Piece ' + n] = s.title;
        });
        payload = {
          items: [{
            id: parseInt(WRAPPER_VARIANT_ID, 10),
            quantity: 1,
            properties: lineItemProperties
          }]
        };
      } else {
        // Default path: 4 real line items, each tagged for the bundle discount
        payload = {
          items: slots.map(function (s, i) {
            var props = {};
            props[PROPERTY_KEY] = '1';
            props[PROPERTY_KEY + '_slot'] = String(i + 1);
            return { id: parseInt(s.variantId, 10), quantity: 1, properties: props };
          })
        };
      }

      fetch(routeRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || ERROR_TEXT); });
        window.location.href = routeRoot + 'checkout';
      }).catch(function (err) {
        showError(err.message || ERROR_TEXT);
        checkoutBtn.disabled = false;
        checkoutBtn.textContent = origText;
        checkoutBtn.classList.add('pick4__checkout-btn--active');
      });
    });

    updateSlotsUI();

    /* Hide the bundle checkout bar when ANY site overlay is open (mobile nav,
       cart drawer, search drawer, modal). AylaGleam runs the same Prestige base
       as the reference: <header-menu-drawer>, <drawer>, etc.
       Detection: body/html overflow:hidden (set by the theme on overlay open),
       OR any drawer/modal element with [active]/[open], OR aria-expanded triggers,
       OR legacy body-class indicators. */
    (function watchOverlayState() {
      var bar = section.querySelector('.pick4__checkout-bar');
      if (!bar) return;

      function isOverlayOpen() {
        // Method 1: body/html overflow hidden (most overlay opens, incl. our modal)
        var bodyOverflow = window.getComputedStyle(document.body).overflow;
        var htmlOverflow = window.getComputedStyle(document.documentElement).overflow;
        if (bodyOverflow === 'hidden' || htmlOverflow === 'hidden') return true;

        // Method 2: Prestige's custom drawer elements have [active] when open
        if (document.querySelector('header-menu-drawer[active], drawer[active], modal-dialog[active], dialog[open]')) return true;

        // Method 3: any element with aria-expanded="true" controlling a drawer/modal
        var triggers = document.querySelectorAll('[aria-controls][aria-expanded="true"]');
        for (var i = 0; i < triggers.length; i++) {
          var ctl = triggers[i].getAttribute('aria-controls') || '';
          if (/drawer|menu|modal|popover/i.test(ctl)) return true;
        }

        // Method 4: legacy body class indicators (fallback)
        var cls = document.body.className || '';
        var indicators = ['menu-open', 'mobile-menu-open', 'nav-open', 'js-menu-open', 'drawer-open', 'cart-drawer-open', 'modal-open', 'no-scroll'];
        if (indicators.some(function (c) { return cls.indexOf(c) !== -1; })) return true;

        return false;
      }

      function update() {
        bar.style.display = isOverlayOpen() ? 'none' : '';
      }

      // Watch body + html attribute changes (covers most overlay implementations)
      var mo = new MutationObserver(update);
      mo.observe(document.body, { attributes: true });
      mo.observe(document.documentElement, { attributes: true });
      // Also watch for new elements appearing (Prestige drawers render content lazily)
      mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['active', 'open', 'aria-expanded', 'aria-hidden', 'class'] });

      // Initial check + on every click (covers cases the observer misses)
      update();
      document.addEventListener('click', function () { setTimeout(update, 50); }, true);
    })();

    applyFiltersAndSort();
  }

  /* ---------------- init ---------------- */
  function initAll() {
    document.querySelectorAll('[data-pick4-builder]').forEach(initBuilder);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector && e.target.querySelector('[data-pick4-builder]');
    if (root) initBuilder(root);
  });
})();
